import { useEffect, useMemo, useState, useRef, lazy, Suspense } from 'react';
import { calculateLevel } from '@big2/shared';
import { useGameState } from '../net/GameProvider.js';
import { useI18n } from '../i18n.js';
import { useCardBack } from '../cardBacks.js';
import { LobbyScreen } from './LobbyScreen.js';
import { GameScreen } from './GameScreen.js';
import { WelcomeScreen } from './WelcomeScreen.js';
import { CardBackModal } from './CardBackModal.js';
import { ProfilePage } from './ProfilePage.js';
import { PaymentHistoryPage } from './payment/PaymentHistoryPage.js';
import { RoomHistory } from './RoomHistory.js';
import { NotFoundPage } from './NotFoundPage.js';
import { AuthModal } from './auth/AuthModal.js';
import type { AuthViewMode } from './profile/AuthForms.js';
import { WinCelebration } from './WinCelebration.js';
import { ReconnectModal } from './ReconnectModal.js';
import { ActiveMatchBanner, type ActiveMatchInfo } from './ActiveMatchBanner.js';
import { parseRoute, routeToPath, isModalRoute, type AppRoute } from '../router.js';
import { AppHeader } from './AppHeader.js';
import { computeSettlementData } from '../utils/settlementData.js';
import { copyTextToClipboard } from '../i18n.js';
import { trackEvent } from '../analytics.js';
import { useSafeBackdropDismiss } from '../utils/modalUtils.js';

// Lazily split heavy secondary modals to minimize initial landing page bundle
const RulesModal = lazy(() => import('./RulesModal.js').then((m) => ({ default: m.RulesModal })));
const AdminPanel = lazy(() => import('./AdminPanel.js').then((m) => ({ default: m.AdminPanel })));
const InfoPages = lazy(() => import('./InfoPages.js').then((m) => ({ default: m.InfoPages })));
const FeedbackModal = lazy(() => import('./FeedbackModal.js').then((m) => ({ default: m.FeedbackModal })));

export function App() {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const { cardBack, setCardBack } = useCardBack();
  const [headerCopied, setHeaderCopied] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState<AuthViewMode>('signup');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackErrorContext, setFeedbackErrorContext] = useState<string | null>(null);

  const openFeedback = (errorContext?: string | null) => {
    setFeedbackErrorContext(errorContext ?? null);
    setShowFeedbackModal(true);
  };

  const openAuth = (view: AuthViewMode = 'signup') => {
    setAuthModalView(view);
    setShowAuthModal(true);
  };

  const [xpNotice, setXpNotice] = useState<{ amount: number; levelUp?: number } | null>(null);
  const prevXpRef = useRef<number | null>(null);
  const xpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const currentXp = state.profile?.xp;
    if (currentXp !== undefined && currentXp !== null) {
      if (prevXpRef.current !== null && currentXp > prevXpRef.current) {
        const gained = currentXp - prevXpRef.current;
        const prevLevel = calculateLevel(prevXpRef.current, lang === 'mn' ? 'mn' : 'en').level;
        const currentLevel = calculateLevel(currentXp, lang === 'mn' ? 'mn' : 'en').level;
        const levelUp = currentLevel > prevLevel ? currentLevel : undefined;

        setXpNotice({ amount: gained, levelUp });
        if (xpTimerRef.current) clearTimeout(xpTimerRef.current);
        xpTimerRef.current = setTimeout(() => {
          setXpNotice(null);
        }, 4500);
      }
      prevXpRef.current = currentXp;
    }
    return () => {
      if (xpTimerRef.current) clearTimeout(xpTimerRef.current);
    };
  }, [state.profile?.xp, lang]);

  const [activeMatch, setActiveMatch] = useState<ActiveMatchInfo | null>(() => {
    try {
      const saved = localStorage.getItem('big2.activeMatch');
      if (saved) {
        const parsed = JSON.parse(saved) as ActiveMatchInfo;
        if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          return parsed;
        }
        localStorage.removeItem('big2.activeMatch');
      }
    } catch {}
    return null;
  });

  useEffect(() => {
    if (state.roomId && state.game?.phase === 'playing') {
      if (activeMatch?.roomId === state.roomId) return;
      const myPlayerName =
        state.game.players.find((p) => p.playerId === state.playerId)?.name ||
        state.profile?.displayName ||
        (typeof localStorage !== 'undefined' ? localStorage.getItem('big2.name') : null) ||
        undefined;
      const info: ActiveMatchInfo = {
        roomId: state.roomId,
        gameType: state.game.gameType,
        playerName: myPlayerName,
        timestamp: Date.now(),
      };
      setActiveMatch(info);
      try {
        localStorage.setItem('big2.activeMatch', JSON.stringify(info));
      } catch {}
    } else if (state.lastGameOver?.matchOver) {
      if (!activeMatch) return;
      setActiveMatch(null);
      try {
        localStorage.removeItem('big2.activeMatch');
      } catch {}
    }
  }, [state.roomId, state.game?.phase, state.game?.players, state.playerId, state.lastGameOver?.matchOver, state.profile?.displayName, activeMatch]);

  useEffect(() => {
    if (state.error && activeMatch) {
      const err = state.error.toLowerCase();
      if (err.includes('not found') || err.includes('already taken') || err.includes('finished')) {
        setActiveMatch(null);
        try {
          localStorage.removeItem('big2.activeMatch');
        } catch {}
      }
    }
  }, [state.error, activeMatch]);

  const handleRejoinActiveMatch = () => {
    if (!activeMatch) return;
    const name =
      state.profile?.displayName ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('big2.name') : null) ||
      activeMatch.playerName ||
      'Player';
    actions.joinRoom(activeMatch.roomId, name);
    navigate({ type: 'room', roomId: activeMatch.roomId });
  };

  const handleDismissActiveMatch = () => {
    setActiveMatch(null);
    try {
      localStorage.removeItem('big2.activeMatch');
    } catch {}
  };

  const [currentRoute, setCurrentRoute] = useState<AppRoute>(() =>
    parseRoute(
      typeof window !== 'undefined' ? window.location.pathname : '/',
      typeof window !== 'undefined' ? window.location.search : '',
    )
  );

  const navigate = (next: AppRoute, replace = false) => {
    const targetPath = routeToPath(next);
    if (typeof window !== 'undefined' && window.location.pathname !== targetPath) {
      if (replace) {
        window.history.replaceState({ modal: isModalRoute(next) }, '', targetPath);
      } else {
        window.history.pushState({ modal: isModalRoute(next) }, '', targetPath);
      }
    }
    if (next.type === 'rules') {
      trackEvent('modal_open', { modal: 'rules', game_type: next.gameType });
      trackEvent('rules_view', { game_type: next.gameType });
      trackEvent(`rules_view_${next.gameType}`);
    } else if (next.type === 'admin') {
      trackEvent('modal_open', { modal: 'admin' });
    } else if (next.type === 'info') {
      trackEvent('modal_open', { modal: `info_${next.page}` });
    }
    setCurrentRoute(next);
  };

  const closeCurrentModal = () => {
    navigate(state.roomId ? { type: 'room', roomId: state.roomId } : { type: 'home' });
  };

  const adminBackdropHandlers = useSafeBackdropDismiss(closeCurrentModal);

  const isMn = lang === 'mn';
  const activeGameType = state.game?.gameType ?? state.lobby?.config.gameType ?? 'big2';

  useEffect(() => {
    const onPopState = () => {
      const next = parseRoute(window.location.pathname, window.location.search);
      if (state.roomId && next.type !== 'room') {
        const inGame = !!state.game && (state.game.phase === 'playing' || !!state.lastGameOver);
        if (inGame) {
          const leave = window.confirm(
            isMn
              ? 'Өрөөнөөс гарч эхлэл хуудас руу буцах уу?'
              : 'Leave the current room and return to the home page?'
          );
          if (!leave) {
            window.history.pushState(null, '', `/room/${state.roomId}`);
            return;
          }
        }
        actions.leaveRoom();
      }
      setCurrentRoute(next);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [state.roomId, state.game, state.lastGameOver, isMn, actions]);

  const autoJoinedRoomRef = useRef<string | null>(null);

  // Keep room URL synchronized with active room state
  useEffect(() => {
    if (state.roomId) {
      autoJoinedRoomRef.current = state.roomId;
    } else if (currentRoute.type === 'home') {
      autoJoinedRoomRef.current = null;
    }
  }, [state.roomId, currentRoute.type]);

  const prevRoomIdRef = useRef<string | null>(state.roomId);

  // Keep room URL synchronized with active room state
  useEffect(() => {
    const prevRoomId = prevRoomIdRef.current;
    prevRoomIdRef.current = state.roomId;

    if (state.roomId) {
      if (
        (currentRoute.type === 'home' && prevRoomId !== state.roomId) ||
        (currentRoute.type === 'room' && currentRoute.roomId !== state.roomId)
      ) {
        navigate({ type: 'room', roomId: state.roomId }, currentRoute.type === 'home');
      }
    } else {
      if (currentRoute.type === 'room') {
        navigate({ type: 'home' }, true);
      }
    }
  }, [state.roomId, currentRoute.type]);

  // Auto-join room when directly loading /room/:roomId (guard against re-joining on explicit exit)
  useEffect(() => {
    if (
      currentRoute.type === 'room' &&
      !state.roomId &&
      state.connectionStatus === 'connected' &&
      autoJoinedRoomRef.current !== currentRoute.roomId
    ) {
      autoJoinedRoomRef.current = currentRoute.roomId;
      const savedName = localStorage.getItem('big2.name') || 'Player';
      actions.joinRoom(currentRoute.roomId, savedName);
    }
  }, [currentRoute, state.roomId, state.connectionStatus, actions]);

  const showRules = currentRoute.type === 'rules';
  const rulesGameType = currentRoute.type === 'rules' ? currentRoute.gameType : 'big2';
  const showCardBacks = currentRoute.type === 'cardBacks';
  const showProfile = currentRoute.type === 'profile';
  const profileTab = currentRoute.type === 'profile' ? currentRoute.tab ?? 'profile' : 'profile';
  const showHistory = currentRoute.type === 'history';
  const showPayments = currentRoute.type === 'payments';
  const infoPage = currentRoute.type === 'info' ? currentRoute.page : null;
  const showAdminRoute = currentRoute.type === 'admin';
  const isNotFound = currentRoute.type === 'notFound';

  // Automatically clear server error toasts after 20 seconds
  useEffect(() => {
    if (!state.error) return;
    const timer = setTimeout(() => {
      actions.clearError();
    }, 20_000);
    return () => clearTimeout(timer);
  }, [state.error, actions]);

  // Only Paid Tier users can use custom card backs; reset to classic_blue otherwise
  useEffect(() => {
    if (state.profile?.tier !== 'paid' && cardBack !== 'classic_blue') {
      setCardBack('classic_blue');
    }
  }, [state.profile?.tier, cardBack, setCardBack]);

  // Automatically open AuthModal when landing from a password recovery email link
  useEffect(() => {
    if (state.recoveryToken || state.authNotice?.kind === 'password_reset_ready') {
      openAuth('reset');
    }
  }, [state.recoveryToken, state.authNotice?.kind]);

  const handleCopyHeaderCode = async () => {
    if (!state.roomId) return;
    const ok = await copyTextToClipboard(state.roomId);
    if (ok) {
      trackEvent('room_code_copied', { source: 'header', room_id: state.roomId });
      setHeaderCopied(true);
      setTimeout(() => setHeaderCopied(false), 2000);
    }
  };

  const realWinData = useMemo(() => computeSettlementData(state), [
    state.lastGameOver,
    state.game,
    state.lobby,
    state.playerId,
  ]);

  const isHost = !!state.lobby && state.lobby.hostId === state.playerId;

  const isInGame = !!state.roomId && !!state.game && (state.game.phase === 'playing' || !!state.lastGameOver);

  // Accidental Disconnect Handling:
  // Grace period prevents jarring modal flickers during quick tab switches or micro-reconnections (< 1.2s).
  const isDisconnectedInRoom =
    state.connectionStatus !== 'connected' && Boolean(state.roomId || state.lastRoomId);
  const [showReconnectModal, setShowReconnectModal] = useState(false);

  useEffect(() => {
    if (!isDisconnectedInRoom) {
      setShowReconnectModal(false);
      return;
    }

    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      setShowReconnectModal(false);
      return;
    }

    const timer = setTimeout(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        setShowReconnectModal(true);
      }
    }, 1200);

    return () => clearTimeout(timer);
  }, [isDisconnectedInRoom]);

  // When returning to tab, reset and give a fresh grace window for the socket to reconnect
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setShowReconnectModal(false);
        if (state.connectionStatus !== 'connected' && Boolean(state.roomId || state.lastRoomId)) {
          const timer = setTimeout(() => {
            if (
              document.visibilityState === 'visible' &&
              state.connectionStatus !== 'connected' &&
              Boolean(state.roomId || state.lastRoomId)
            ) {
              setShowReconnectModal(true);
            }
          }, 1200);
          return () => clearTimeout(timer);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.connectionStatus, state.roomId, state.lastRoomId]);

  const handleLeaveRoomFromReconnect = () => {
    actions.leaveRoom();
    navigate({ type: 'home' });
  };

  return (
    <div className={`app-container${isInGame ? ' is-in-game' : ''}`}>
      {/* Top Bar */}
      <AppHeader
        activeGameType={activeGameType}
        headerCopied={headerCopied}
        onCopyHeaderCode={handleCopyHeaderCode}
        onOpenAuth={openAuth}
        onOpenFeedback={() => openFeedback()}
        navigate={navigate}
      />

      {/* Accidental Disconnect Overlay */}
      {showReconnectModal && (
        <ReconnectModal
          roomId={state.roomId}
          lastRoomId={state.lastRoomId}
          onRetry={actions.rejoinRoom}
          onLeave={handleLeaveRoomFromReconnect}
        />
      )}

      {/* Match in Progress / Rejoin Prompt (Rocket League style) */}
      {!state.roomId && activeMatch && (
        <ActiveMatchBanner
          match={activeMatch}
          onRejoin={handleRejoinActiveMatch}
          onDismiss={handleDismissActiveMatch}
        />
      )}

      {/* Main Content Area */}
      <main className="app-main">
        {showPayments ? (
          <PaymentHistoryPage
            onBack={closeCurrentModal}
            onOpenProfile={() => {
              if (state.profile) {
                navigate({ type: 'profile', tab: 'profile' });
              } else {
                openAuth('signin');
              }
            }}
          />
        ) : showProfile ? (
          <ProfilePage
            initialTab={profileTab}
            onTabChange={(tab) => navigate({ type: 'profile', tab }, true)}
            onBack={closeCurrentModal}
            onOpenInfoPage={(page) => navigate({ type: 'info', page })}
            onOpenPayments={() => navigate({ type: 'payments' })}
            onOpenAuth={openAuth}
          />
        ) : isNotFound ? (
          <NotFoundPage onGoHome={() => navigate({ type: 'home' })} />
        ) : !state.roomId ? (
          <WelcomeScreen
            initialGameType={currentRoute.type === 'home' ? currentRoute.gameType : undefined}
            onOpenRules={(gt) => navigate({ type: 'rules', gameType: gt ?? activeGameType })}
            onOpenCardBacks={() => navigate({ type: 'cardBacks' })}
            onOpenProfile={() => {
              if (state.profile) {
                navigate({ type: 'profile', tab: 'profile' });
              } else {
                openAuth('signup');
              }
            }}
            onOpenInfoPage={(page) => navigate({ type: 'info', page })}
          />
        ) : state.game && (state.game.phase === 'playing' || !!state.lastGameOver) ? (
          <GameScreen
            onOpenProfile={() => {
              if (state.profile) {
                navigate({ type: 'profile', tab: 'profile' });
              } else {
                openAuth('signin');
              }
            }}
          />
        ) : state.lobby ? (
          <LobbyScreen
            onOpenCardBacks={() => navigate({ type: 'cardBacks' })}
            onOpenProfile={() => {
              if (state.profile) {
                navigate({ type: 'profile', tab: 'profile' });
              } else {
                openAuth('signin');
              }
            }}
            onOpenHistory={() => navigate({ type: 'history' })}
            onLeaveRoom={() => navigate({ type: 'home' })}
          />
        ) : null}
      </main>

      {/* Winning Confetti + 700ms Delayed Round Settlement Modal */}
      {realWinData && (
        <WinCelebration
          data={realWinData}
          canPlayAgain={isHost}
          onPlayAgain={actions.startGame}
          onClose={actions.clearGameOver}
        />
      )}

      {/* Modals */}
      {showAuthModal && (
        <AuthModal
          initialView={authModalView}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => navigate({ type: 'profile', tab: 'profile' })}
          onOpenInfoPage={(page) => navigate({ type: 'info', page })}
        />
      )}
      {showRules && (
        <Suspense fallback={null}>
          <RulesModal
            onClose={closeCurrentModal}
            defaultGameType={rulesGameType}
            daaluuEnabled={state.supportedGames.includes('daaluu')}
            twentyOneEnabled={state.supportedGames.includes('twentyone')}
            lockedGameType={state.roomId ? activeGameType : undefined}
            onSelectGameType={(gt) => navigate({ type: 'rules', gameType: gt }, true)}
          />
        </Suspense>
      )}
      {showCardBacks && activeGameType !== 'daaluu' && (
        <CardBackModal
          onClose={closeCurrentModal}
          onOpenProfile={() => {
            if (state.profile) {
              navigate({ type: 'profile', tab: 'profile' });
            } else {
              openAuth('signup');
            }
          }}
        />
      )}
      {showHistory && <RoomHistory onClose={closeCurrentModal} />}
      {infoPage && (
        <Suspense fallback={null}>
          <InfoPages
            page={infoPage}
            onNavigate={(page) => (page ? navigate({ type: 'info', page }) : closeCurrentModal())}
            onOpenProfile={() => navigate({ type: 'profile', tab: 'profile' })}
            onOpenCardBacks={activeGameType === 'daaluu' ? undefined : () => navigate({ type: 'cardBacks' })}
          />
        </Suspense>
      )}
      {showAdminRoute && (
        state.profile?.isAdmin ? (
          <Suspense fallback={null}>
            <AdminPanel onClose={closeCurrentModal} />
          </Suspense>
        ) : (
          <div
            className="modal-overlay"
            {...adminBackdropHandlers}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 300,
              padding: 16,
            }}
          >
            <div
              className="modal-card"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#101b16',
                border: '1px solid rgba(251, 191, 36, 0.45)',
                borderRadius: 14,
                maxWidth: 420,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              <h2 style={{ margin: 0, color: '#fde68a' }}>
                🛡️ {isMn ? 'Админ эрх шаардлагатай' : 'Administrator Access Required'}
              </h2>
              <p style={{ margin: 0, fontSize: '0.92em', lineHeight: 1.5 }}>
                {isMn
                  ? 'Админ самбар луу нэвтрэхийн тулд админ эрхтэй хаягаар нэвтэрнэ үү.'
                  : 'Please sign in with an administrator account to open the Administration panel.'}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    navigate({ type: 'profile', tab: 'profile' });
                  }}
                  style={{ background: 'var(--accent-color)', color: '#000', fontWeight: 800 }}
                >
                  {isMn ? 'Профайл / Нэвтрэх' : 'Sign In / Profile'}
                </button>
                <button type="button" onClick={closeCurrentModal}>
                  {isMn ? 'Хаах' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )
      )}

      {/* XP Gained Toast Notification */}
      {xpNotice && (
        <div
          role="status"
          aria-live="polite"
          className="xp-gain-toast"
        >
          <span className="xp-toast-icon">⚡</span>
          <span className="xp-toast-amount">+{xpNotice.amount} XP</span>
          {xpNotice.levelUp && (
            <span className="xp-toast-levelup">
              🎉 {lang === 'mn' ? `${xpNotice.levelUp}-р түвшинд хүрлээ!` : `Level ${xpNotice.levelUp}!`}
            </span>
          )}
          <button
            type="button"
            className="xp-toast-dismiss"
            onClick={() => setXpNotice(null)}
            aria-label={lang === 'mn' ? 'Хаах' : 'Dismiss'}
          >
            ×
          </button>
        </div>
      )}

      {/* Weekly Tsai Bonus Toast */}
      {state.weeklyBonusNotice && (
        <div
          style={{
            position: 'fixed',
            top: 64,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
            color: '#000',
            fontWeight: 800,
            padding: '10px 18px',
            borderRadius: 8,
            zIndex: 300,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            width: 'max-content',
            maxWidth: 'calc(100vw - 24px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          <span>{t.weeklyGrantedToast(state.weeklyBonusNotice)}</span>
          <button
            type="button"
            onClick={actions.clearWeeklyBonusNotice}
            style={{ background: 'transparent', padding: '2px 6px', border: '1px solid rgba(0,0,0,0.35)' }}
          >
            ×
          </button>
        </div>
      )}

      {/* Error Toast */}
      {state.error && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#f87171',
            color: '#000',
            padding: '12px 18px',
            borderRadius: 8,
            zIndex: 300,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            width: 'max-content',
            maxWidth: 'calc(100vw - 24px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          <span style={{ fontSize: '0.92em', fontWeight: 600 }}>{t.translateServerError(state.error)}</span>
          <button
            type="button"
            onClick={() => openFeedback(state.error)}
            style={{
              background: 'rgba(0,0,0,0.15)',
              border: '1px solid rgba(0,0,0,0.25)',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: '0.82em',
              fontWeight: 600,
              cursor: 'pointer',
              color: '#000',
            }}
          >
            {isMn ? 'Мэдэгдэх' : 'Report'}
          </button>
          <button
            onClick={actions.clearError}
            style={{ background: 'transparent', padding: '2px 8px', border: '1px solid rgba(0,0,0,0.3)', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      {showFeedbackModal && (
        <Suspense fallback={null}>
          <FeedbackModal
            onClose={() => {
              setShowFeedbackModal(false);
              setFeedbackErrorContext(null);
            }}
            initialError={feedbackErrorContext}
          />
        </Suspense>
      )}
    </div>
  );
}
